/** Scene renderers: canvas (screen / PNG export) and SVG (vector export). */
import type { Scene } from './scene';

export function renderCanvas(c: CanvasRenderingContext2D, s: Scene, scale: number) {
  c.setTransform(scale, 0, 0, scale, 0, 0);
  const clip = () => {
    c.save();
    c.beginPath();
    c.rect(s.plot.x, s.plot.y, s.plot.w, s.plot.h);
    c.clip();
  };
  for (const p of s.prims) {
    const clipped = 'clip' in p && p.clip;
    if (clipped) clip();
    switch (p.t) {
      case 'rect':
        c.fillStyle = p.fill;
        c.fillRect(p.x, p.y, p.w, p.h);
        break;
      case 'path':
        c.beginPath();
        c.moveTo(p.pts[0], p.pts[1]);
        for (let i = 2; i < p.pts.length; i += 2) c.lineTo(p.pts[i], p.pts[i + 1]);
        c.strokeStyle = p.stroke;
        c.lineWidth = p.width;
        c.lineJoin = 'round';
        c.setLineDash(p.dash ?? []);
        c.stroke();
        c.setLineDash([]);
        break;
      case 'area':
        c.beginPath();
        c.moveTo(p.pts[0], p.pts[1]);
        for (let i = 2; i < p.pts.length; i += 2) c.lineTo(p.pts[i], p.pts[i + 1]);
        c.closePath();
        c.fillStyle = p.fill;
        c.fill();
        break;
      case 'circle':
        c.beginPath();
        c.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        c.fillStyle = p.fill;
        c.fill();
        if (p.stroke) {
          c.strokeStyle = p.stroke;
          c.lineWidth = 1;
          c.stroke();
        }
        break;
      case 'text':
        c.save();
        c.font = `${p.weight ?? 400} ${p.size}px ${p.mono ? s.theme.mono : s.theme.font}`;
        c.fillStyle = p.fill;
        c.textAlign = p.align;
        c.textBaseline = p.baseline === 'bottom' ? 'alphabetic' : p.baseline;
        c.translate(p.x, p.y);
        if (p.rotate) c.rotate(-Math.PI / 2);
        c.fillText(p.text, 0, p.baseline === 'bottom' ? -2 : 0);
        c.restore();
        break;
    }
    if (clipped) c.restore();
  }
}

const esc = (t: string) => t.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

/** Parse rgba()/hex colour into SVG colour + opacity. */
function paint(color: string): string {
  const m = color.match(/rgba\(\s*([\d.]+)\s*,\s*([\d.]+)\s*,\s*([\d.]+)\s*,\s*([\d.]+)\s*\)/);
  if (m) return `rgb(${m[1]},${m[2]},${m[3]})" fill-opacity="${m[4]}" stroke-opacity="${m[4]}`;
  return color;
}

export function renderSvg(s: Scene): string {
  const out: string[] = [];
  const pts = (a: number[]) => {
    const r: string[] = [];
    for (let i = 0; i < a.length; i += 2) r.push(`${a[i].toFixed(2)},${a[i + 1].toFixed(2)}`);
    return r.join(' ');
  };
  out.push(
    `<svg xmlns="http://www.w3.org/2000/svg" width="${s.width}" height="${s.height}" viewBox="0 0 ${s.width} ${s.height}" font-family="${esc(s.theme.font)}">`,
  );
  out.push(
    `<defs><clipPath id="plotclip"><rect x="${s.plot.x}" y="${s.plot.y}" width="${s.plot.w}" height="${s.plot.h}"/></clipPath></defs>`,
  );
  for (const p of s.prims) {
    const clip = 'clip' in p && p.clip ? ' clip-path="url(#plotclip)"' : '';
    switch (p.t) {
      case 'rect':
        out.push(`<rect x="${p.x}" y="${p.y}" width="${p.w}" height="${p.h}" fill="${paint(p.fill)}"/>`);
        break;
      case 'path':
        out.push(
          `<polyline points="${pts(p.pts)}" fill="none" stroke="${paint(p.stroke)}" stroke-width="${p.width}" stroke-linejoin="round"${
            p.dash?.length ? ` stroke-dasharray="${p.dash.join(' ')}"` : ''
          }${clip}/>`,
        );
        break;
      case 'area':
        out.push(`<polygon points="${pts(p.pts)}" fill="${paint(p.fill)}"${clip}/>`);
        break;
      case 'circle':
        out.push(
          `<circle cx="${p.x.toFixed(2)}" cy="${p.y.toFixed(2)}" r="${p.r}" fill="${paint(p.fill)}"${p.stroke ? ` stroke="${p.stroke}"` : ''}${clip}/>`,
        );
        break;
      case 'text': {
        const anchor = p.align === 'left' ? 'start' : p.align === 'right' ? 'end' : 'middle';
        const base = p.baseline === 'top' ? 'hanging' : p.baseline === 'middle' ? 'central' : 'auto';
        const tr = p.rotate ? ` transform="rotate(-90 ${p.x} ${p.y})"` : '';
        const fam = p.mono ? ` font-family="${esc(s.theme.mono)}"` : '';
        out.push(
          `<text x="${p.x}" y="${p.baseline === 'bottom' ? p.y - 2 : p.y}" fill="${paint(p.fill)}" font-size="${p.size}" font-weight="${p.weight ?? 400}" text-anchor="${anchor}" dominant-baseline="${base}"${tr}${fam}>${esc(p.text)}</text>`,
        );
        break;
      }
    }
  }
  out.push('</svg>');
  return out.join('\n');
}
